import {
  ZonedDateTime,
  parseZonedDateTime,
  getLocalTimeZone,
  now,
} from '@internationalized/date';
import { destr } from 'destr';
import type {
  QueryTab,
  QueryVersion,
  SerializedDateRange,
  SerializedQueryTab,
} from '@/types/query';

// Serialize ZonedDateTime to string
export function serializeDateRange(dateRange: {
  start: ZonedDateTime;
  end: ZonedDateTime;
}): SerializedDateRange {
  return {
    start: dateRange.start.toString(),
    end: dateRange.end.toString(),
  };
}

// Deserialize string to ZonedDateTime
export function deserializeDateRange(serialized: SerializedDateRange): {
  start: ZonedDateTime;
  end: ZonedDateTime;
} {
  try {
    const start = parseZonedDateTime(serialized.start);
    const end = parseZonedDateTime(serialized.end);

    // Ensure these are proper ZonedDateTime instances
    if (!start || !end || !start.timeZone || !end.timeZone) {
      throw new Error('Invalid ZonedDateTime after parsing');
    }

    return { start, end };
  } catch (error) {
    console.error('Failed to deserialize date range:', error, serialized);
    // Return a default date range instead of crashing
    const timezone = getLocalTimeZone();
    const currentDateTime = now(timezone);
    const startOfDay = currentDateTime.set({
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0,
    });
    return { start: startOfDay, end: currentDateTime };
  }
}

// Serialize tabs for localStorage
export function serializeTabsForStorage(tabs: QueryTab[]): string {
  const serialized: SerializedQueryTab[] = tabs.map((tab) => ({
    ...tab,
    dateRange: serializeDateRange(tab.dateRange),
    // Also serialize dateRange in versions if they exist
    versions: tab.versions?.map((version) => ({
      ...version,
      dateRange: serializeDateRange(version.dateRange),
    })),
    queriedDataRange: tab.queriedDataRange
      ? serializeDateRange(tab.queriedDataRange)
      : undefined,
    histogram: tab.histogram
      ? {
          timeRanges: tab.histogram.timeRanges.map((range) =>
            serializeDateRange(range),
          ),
          interval: tab.histogram.interval,
        }
      : {
          timeRanges: [],
          interval: '',
        },
  }));
  return JSON.stringify(serialized);
}

// Deserialize tabs from localStorage
export function deserializeTabsFromStorage(data: string): QueryTab[] {
  try {
    const parsed = destr<SerializedQueryTab[]>(data);

    // If parsed is not an array, return empty array
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.map((tab) => ({
      ...tab,
      dateRange: deserializeDateRange(tab.dateRange),
      // Also deserialize dateRange in versions if they exist
      versions: tab.versions?.map(
        (version): QueryVersion => ({
          ...version,
          dateRange: deserializeDateRange(version.dateRange),
        }),
      ),
      queriedDataRange: tab.queriedDataRange
        ? deserializeDateRange(tab.queriedDataRange)
        : undefined,
      histogram: tab.histogram
        ? {
            timeRanges: tab.histogram.timeRanges.map((range) =>
              deserializeDateRange(range),
            ),
            interval: tab.histogram.interval,
          }
        : {
            timeRanges: [],
            interval: '',
          },
    }));
  } catch (error) {
    console.error('Failed to deserialize tabs:', error);
    return [];
  }
}

// Prune old versions to stay within storage limits
export function pruneVersions(
  versions: QueryVersion[] | undefined,
  maxVersions = 20,
): QueryVersion[] | undefined {
  if (!versions) return versions;
  if (versions.length <= maxVersions) return versions;

  // Keep the most recent versions
  return versions
    .sort(
      (a: QueryVersion, b: QueryVersion) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    )
    .slice(0, maxVersions);
}
